"use client";

import { useEffect, useRef } from "react";

export default function DitherGeometricHero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = (canvas.width = canvas.offsetWidth);
    let height = (canvas.height = canvas.offsetHeight);

    // Geometry: Sphere of points
    const points: { x: number; y: number; z: number }[] = [];
    const numPoints = 800; // Slightly reduced point count for cleaner look
    const radius = Math.min(width, height) * 0.35;

    // Create points on a sphere (Fibonacci Sphere for even distribution)
    const phi = Math.PI * (3 - Math.sqrt(5)); // golden angle
    for (let i = 0; i < numPoints; i++) {
      const y = 1 - (i / (numPoints - 1)) * 2; // y goes from 1 to -1
      const r = Math.sqrt(1 - y * y); // radius at y
      const theta = phi * i; // golden angle increment

      const x = Math.cos(theta) * r;
      const z = Math.sin(theta) * r;

      points.push({
        x: x * radius,
        y: y * radius,
        z: z * radius,
      });
    }

    let rotationX = 0;
    let rotationY = 0;
    let zoom = 1; // Zoom factor
    const zoomSpeed = 0.0005; // Slower, subtle breathing

    // Dither matrix (4x4 Bayer)
    const bayerMatrix = [
      [0, 8, 2, 10],
      [12, 4, 14, 6],
      [3, 11, 1, 9],
      [15, 7, 13, 5],
    ];

    let animationId: number;

    const animate = () => {
      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, width, height);

      // Use theme color or fallback
      ctx.fillStyle = "rgb(255, 153, 0)";

      rotationX += 0.001;
      rotationY += 0.002;

      // Oscillate zoom for a "breathing" effect
      zoom = 1 + Math.sin(Date.now() * zoomSpeed) * 0.1;

      // Center of rotation
      const cx = width * 0.75; // Shift to right side
      const cy = height * 0.5;

      points.forEach((p) => {
        // Rotate Y
        let x = p.x * Math.cos(rotationY) - p.z * Math.sin(rotationY);
        let z = p.x * Math.sin(rotationY) + p.z * Math.cos(rotationY);
        let y = p.y;

        // Rotate X
        const y_new = y * Math.cos(rotationX) - z * Math.sin(rotationX);
        z = y * Math.sin(rotationX) + z * Math.cos(rotationX);
        y = y_new;

        // Apply Zoom
        x *= zoom;
        y *= zoom;
        z *= zoom;

        // Perspective
        const scale = 1000 / (1000 + z);
        const x2d = x * scale + cx;
        const y2d = y * scale + cy;

        // Only draw if within bounds
        if (x2d < 0 || x2d > width || y2d < 0 || y2d > height) return;

        // Depth value normalized 0-1 (back to front)
        const depth = (z + radius * zoom) / (2 * radius * zoom);
        // Invert depth for rendering (front is brighter/larger)
        const intensity = 1 - depth;

        // Dither Logic
        // Map position to Bayer matrix
        const mx = Math.floor(x2d) % 4;
        const my = Math.floor(y2d) % 4;
        const threshold = bayerMatrix[my][mx] / 16;

        // If intensity > threshold, draw pixel
        if (intensity > threshold - 0.15) {
          // Adjusted threshold for cleaner look
          // Increased pixel size for better visibility
          const pixelSize = 5;
          const xSnap = Math.floor(x2d / pixelSize) * pixelSize;
          const ySnap = Math.floor(y2d / pixelSize) * pixelSize;

          // Dynamic opacity based on depth
          ctx.globalAlpha = 0.5 + intensity * 0.5;
          ctx.fillRect(xSnap, ySnap, pixelSize, pixelSize);
        }
      });

      animationId = requestAnimationFrame(animate);
    };

    const handleResize = () => {
      if (canvas) {
        width = canvas.width = canvas.offsetWidth;
        height = canvas.height = canvas.offsetHeight;
      }
    };

    window.addEventListener("resize", handleResize);
    animationId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none mix-blend-multiply dark:mix-blend-screen opacity-70"
    />
  );
}
